import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleUserRound,
  ExternalLink,
  Filter,
  ImageOff,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  RotateCcw,
  Search,
  SlidersHorizontal,
  UserRoundCheck,
  UsersRound,
} from "lucide-react";
import { api, asItems, asObject, formatApiError, formatPrice, prefixUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const PAGE_SIZE = 20;
const LEAD_PAGE_SIZE = 25;
const ADVANCED_FILTER_KEYS = ["source", "property_status", "followup", "visit_status", "sort"];
const FILTER_KEYS = [
  "q", "agent_id", "office_id", "property_id", "property_type", "lead_status",
  "assignment", "date_from", "date_to", "source", "property_status", "followup",
  "visit_status", "sort",
];

const EMPTY_FILTERS = {
  q: "",
  agent_id: "",
  office_id: "",
  property_id: "",
  property_type: "",
  lead_status: "",
  assignment: "",
  date_from: "",
  date_to: "",
  source: "",
  property_status: "",
  followup: "",
  visit_status: "",
  sort: "latest",
};

const ASSIGNMENT_OPTIONS = [
  { value: "", label: "All assignments" },
  { value: "ASSIGNED", label: "Assigned" },
  { value: "UNASSIGNED", label: "Unassigned" },
];

const FOLLOWUP_OPTIONS = [
  { value: "", label: "Any follow-up" },
  { value: "OVERDUE", label: "Overdue" },
  { value: "DUE_TODAY", label: "Due today" },
  { value: "UPCOMING", label: "Upcoming" },
  { value: "OPEN", label: "Any open" },
  { value: "NONE", label: "No open follow-up" },
];

const SORT_OPTIONS = [
  { value: "latest", label: "Latest activity" },
  { value: "leads_desc", label: "Most leads" },
  { value: "unassigned_desc", label: "Most unassigned" },
  { value: "followup_asc", label: "Next follow-up" },
  { value: "property_name", label: "Property name" },
];

function filtersFromParams(params) {
  return FILTER_KEYS.reduce((result, key) => {
    result[key] = params.get(key) || EMPTY_FILTERS[key];
    return result;
  }, {});
}

function requestParams(params, extras = {}) {
  const values = {};
  FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) values[key] = value;
  });
  return { ...values, ...extras };
}

function optionRows(rows, emptyLabel, humanize = false) {
  const options = [{ value: "", label: emptyLabel }];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.value != null && String(row.value).trim()) {
      const label = String(row.label || row.value);
      options.push({ value: String(row.value), label: humanize ? displayLabel(label) : label });
    }
  });
  return options;
}

function displayLabel(value) {
  if (!value) return "—";
  const normalized = String(value).toUpperCase();
  if (normalized === "PLOT_DEMAND") return "Plots on Demand";
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value, dateOnly = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", dateOnly
    ? { day: "2-digit", month: "short", year: "numeric" }
    : { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    .format(date);
}

function count(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function parseRelationshipList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

function propertyLink(id) {
  return `/dashboard/admin/properties?select=${encodeURIComponent(id)}`;
}

function userLink(id) {
  return `/dashboard/admin/users?select=${encodeURIComponent(id)}`;
}

function officeLink(id) {
  return `/dashboard/admin/offices?select=${encodeURIComponent(id)}`;
}

function leadLink(id) {
  return `/dashboard/admin/leads?select=${encodeURIComponent(id)}`;
}

function plotDemandLink(id) {
  return `/dashboard/admin/plots-on-demand/workbench?select=${encodeURIComponent(id)}`;
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["WON", "SOLD", "BOOKED", "COMPLETED", "PUBLISHED", "VERIFIED"].includes(status)) {
    return "border-emerald-500/40 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-bold";
  }
  if (["LOST", "CANCELLED", "REJECTED", "NO_SHOW"].includes(status)) {
    return "border-rose-500/40 bg-rose-500/15 text-rose-800 dark:text-rose-300 font-bold";
  }
  if (["NEW", "UNASSIGNED", "PENDING", "PENDING_VERIFICATION", "OVERDUE"].includes(status)) {
    return "border-amber-500/40 bg-amber-500/15 text-amber-900 dark:text-amber-300 font-bold";
  }
  return "border-border bg-secondary text-foreground font-bold";
}

export default function AdminLeadExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const [draft, setDraft] = useState(() => filtersFromParams(searchParams));
  const [filterOptions, setFilterOptions] = useState({});
  const [showMore, setShowMore] = useState(() => ADVANCED_FILTER_KEYS.some((key) => searchParams.get(key)));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedPropertyId, setExpandedPropertyId] = useState("");
  const [leadRows, setLeadRows] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadPage, setLeadPage] = useState(1);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState("");

  const requestedPage = Number(searchParams.get("page") || 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? Math.min(requestedPage, 1_000_000) : 1;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const maxLeadPage = Math.max(1, Math.ceil(leadTotal / LEAD_PAGE_SIZE));
  const activeFilterCount = useMemo(() => {
    const appliedParams = new URLSearchParams(queryString);
    return FILTER_KEYS.filter((key) => key !== "sort" && appliedParams.get(key)).length;
  }, [queryString]);

  useEffect(() => {
    const appliedParams = new URLSearchParams(queryString);
    setDraft(filtersFromParams(appliedParams));
    if (ADVANCED_FILTER_KEYS.some((key) => appliedParams.get(key))) setShowMore(true);
  }, [queryString]);

  useEffect(() => {
    let current = true;
    setOptionsLoading(true);
    api.get("/v1/admin/lead-explorer/filters")
      .then((response) => {
        if (current) setFilterOptions(asObject(response.data));
      })
      .catch((requestError) => {
        if (current) {
          setFilterOptions({});
          toast.error(`Lead filters could not be loaded: ${formatApiError(requestError) || "Request failed."}`);
        }
      })
      .finally(() => {
        if (current) setOptionsLoading(false);
      });
    return () => { current = false; };
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    api.get("/v1/admin/lead-explorer", {
      params: requestParams(new URLSearchParams(queryString), { page, limit: PAGE_SIZE }),
    })
      .then((response) => {
        if (!current) return;
        const data = asObject(response.data);
        setRows(asItems(data));
        setSummary(asObject(data.summary));
        setTotal(Number(data.total || 0));
      })
      .catch((requestError) => {
        if (!current) return;
        setRows([]);
        setSummary({});
        setTotal(0);
        setError(formatApiError(requestError));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, [queryString, page, reloadKey]);

  useEffect(() => {
    if (!expandedPropertyId) {
      setLeadRows([]);
      setLeadTotal(0);
      setLeadError("");
      return undefined;
    }
    let current = true;
    setLeadLoading(true);
    setLeadError("");
    api.get(`/v1/admin/lead-explorer/properties/${expandedPropertyId}/leads`, {
      params: requestParams(new URLSearchParams(queryString), { page: leadPage, limit: LEAD_PAGE_SIZE }),
    })
      .then((response) => {
        if (!current) return;
        const data = asObject(response.data);
        setLeadRows(asItems(data));
        setLeadTotal(Number(data.total || 0));
      })
      .catch((requestError) => {
        if (!current) return;
        setLeadRows([]);
        setLeadTotal(0);
        setLeadError(formatApiError(requestError));
      })
      .finally(() => {
        if (current) setLeadLoading(false);
      });
    return () => { current = false; };
  }, [expandedPropertyId, leadPage, queryString, reloadKey]);

  const updateDraft = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const applyFilters = (event) => {
    event.preventDefault();
    const next = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      if (draft[key] && !(key === "sort" && draft[key] === "latest")) next.set(key, draft[key]);
    });
    setExpandedPropertyId("");
    setLeadPage(1);
    setSearchParams(next);
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setShowMore(false);
    setExpandedPropertyId("");
    setLeadPage(1);
    setSearchParams(new URLSearchParams());
  };

  const changePage = (nextPage) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage > 1) next.set("page", String(nextPage));
    else next.delete("page");
    setExpandedPropertyId("");
    setLeadPage(1);
    setSearchParams(next);
  };

  const toggleProperty = (propertyId) => {
    if (expandedPropertyId === propertyId) {
      setExpandedPropertyId("");
      return;
    }
    setLeadPage(1);
    setExpandedPropertyId(propertyId);
  };

  const agents = optionRows(filterOptions.agents, "All agents");
  const offices = optionRows(filterOptions.offices, "All offices");
  const properties = optionRows(filterOptions.properties, "All properties");
  const propertyTypes = optionRows(filterOptions.property_types, "All property types", true);
  const leadStatuses = optionRows(filterOptions.lead_statuses, "All lead statuses", true);
  const sources = optionRows(filterOptions.sources, "All sources", true);
  const propertyStatuses = optionRows(filterOptions.property_statuses, "All property statuses", true);
  const visitStatuses = optionRows(filterOptions.site_visit_statuses, "Any site visit status", true);

  return (
    <div className="space-y-6" data-testid="admin-lead-explorer-page">
      <header className="relative overflow-hidden rounded-3xl border border-border bg-card p-5 shadow-sm sm:p-7">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-warning to-success" />
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-2 flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-muted-foreground">
              <UsersRound className="h-4 w-4 text-warning" />
              Lead Management
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl">
              Lead <span className="brand-gradient-text">Explorer</span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
              Search every property-to-buyer relationship, verify contact and assignment details,
              and open the exact CRM, user, office, or property record without leaving the admin workflow.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/dashboard/admin/leads" title="Open the existing Lead Management Kanban board">
                Kanban Board <ExternalLink />
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReloadKey((value) => value + 1)}
              disabled={loading}
              title="Refresh Lead Explorer data"
            >
              <RefreshCw className={loading ? "animate-spin" : ""} /> Refresh
            </Button>
          </div>
        </div>
      </header>

      <SummaryCards summary={summary} loading={loading} />

      <form onSubmit={applyFilters} className="rounded-3xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-display text-lg font-black text-foreground">
              <SlidersHorizontal className="h-5 w-5 text-primary" /> Search & filters
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Filters are applied on the server and saved in the page URL.
            </p>
          </div>
          {activeFilterCount > 0 && (
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              {activeFilterCount} active {activeFilterCount === 1 ? "filter" : "filters"}
            </Badge>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="relative md:col-span-2 xl:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-muted-foreground">Search</span>
            <Search className="pointer-events-none absolute bottom-2.5 left-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={draft.q}
              onChange={(event) => updateDraft("q", event.target.value)}
              className="pl-9"
              placeholder="Buyer, phone, email, property, code or lead ID"
              title="Search buyers, contact details, properties, codes, or lead IDs"
            />
          </label>
          <FilterSelect label="Agent" value={draft.agent_id} onChange={(value) => updateDraft("agent_id", value)} options={agents} disabled={optionsLoading} />
          <FilterSelect label="Office" value={draft.office_id} onChange={(value) => updateDraft("office_id", value)} options={offices} disabled={optionsLoading} />
          <FilterSelect label="Property" value={draft.property_id} onChange={(value) => updateDraft("property_id", value)} options={properties} disabled={optionsLoading} />
          <FilterSelect label="Property type" value={draft.property_type} onChange={(value) => updateDraft("property_type", value)} options={propertyTypes} disabled={optionsLoading} />
          <FilterSelect label="Lead status" value={draft.lead_status} onChange={(value) => updateDraft("lead_status", value)} options={leadStatuses} disabled={optionsLoading} />
          <FilterSelect label="Assignment" value={draft.assignment} onChange={(value) => updateDraft("assignment", value)} options={ASSIGNMENT_OPTIONS} />
          <DateFilter label="Created from" value={draft.date_from} max={draft.date_to || undefined} onChange={(value) => updateDraft("date_from", value)} />
          <DateFilter label="Created to" value={draft.date_to} min={draft.date_from || undefined} onChange={(value) => updateDraft("date_to", value)} />
        </div>

        <button
          type="button"
          onClick={() => setShowMore((value) => !value)}
          className="mt-4 inline-flex min-h-9 items-center gap-2 rounded-lg px-2 text-sm font-bold text-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={showMore ? "Hide additional filters" : "Show additional filters"}
        >
          <Filter className="h-4 w-4" /> More filters {showMore ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {showMore && (
          <div className="mt-3 grid gap-3 border-t border-border pt-4 md:grid-cols-2 xl:grid-cols-5">
            <FilterSelect label="Source" value={draft.source} onChange={(value) => updateDraft("source", value)} options={sources} disabled={optionsLoading} />
            <FilterSelect label="Property status" value={draft.property_status} onChange={(value) => updateDraft("property_status", value)} options={propertyStatuses} disabled={optionsLoading} />
            <FilterSelect label="Follow-up" value={draft.followup} onChange={(value) => updateDraft("followup", value)} options={FOLLOWUP_OPTIONS} />
            <FilterSelect label="Site visit status" value={draft.visit_status} onChange={(value) => updateDraft("visit_status", value)} options={visitStatuses} disabled={optionsLoading} />
            <FilterSelect label="Sort by" value={draft.sort} onChange={(value) => updateDraft("sort", value)} options={SORT_OPTIONS} />
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={clearFilters} title="Clear every Lead Explorer filter">
            <RotateCcw /> Clear filters
          </Button>
          <Button type="submit" title="Apply the selected Lead Explorer filters">
            <Search /> Apply filters
          </Button>
        </div>
      </form>

      {error && (
        <div className="flex flex-col gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between" role="alert">
          <span className="flex items-center gap-2"><AlertCircle className="h-4 w-4 shrink-0" />{error}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setReloadKey((value) => value + 1)} title="Retry loading Lead Explorer">Retry</Button>
        </div>
      )}

      <section className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
        <div className="flex flex-col gap-2 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div>
            <h2 className="font-display text-xl font-black text-foreground">Properties with matching leads</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {loading ? "Loading property relationships…" : `${count(total)} properties found · expand a row for buyer contact details`}
            </p>
          </div>
          <Badge variant="outline" className="w-fit">Page {page} of {maxPage}</Badge>
        </div>

        {loading ? (
          <PropertySkeletons />
        ) : rows.length === 0 && !error ? (
          <EmptyState onClear={clearFilters} />
        ) : (
          <>
            <div className="hidden overflow-x-auto xl:block">
              <table className="w-full min-w-[1180px] text-left text-sm">
                <thead className="bg-secondary/60 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Property</th>
                    <th className="px-3 py-3">Listed by</th>
                    <th className="px-3 py-3">Assigned team</th>
                    <th className="px-3 py-3">Leads</th>
                    <th className="px-3 py-3">Follow-up & visits</th>
                    <th className="px-3 py-3">Last activity</th>
                    <th className="px-4 py-3 text-right">Buyer details</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((property) => (
                    <Fragment key={property.property_id}>
                      <PropertyDesktopRow
                        property={property}
                        expanded={expandedPropertyId === property.property_id}
                        onToggle={() => toggleProperty(property.property_id)}
                      />
                      {expandedPropertyId === property.property_id && (
                        <tr className="bg-secondary/20">
                          <td colSpan={7} className="p-0">
                            <LeadDetailPanel
                              property={property}
                              rows={leadRows}
                              total={leadTotal}
                              page={leadPage}
                              maxPage={maxLeadPage}
                              loading={leadLoading}
                              error={leadError}
                              onPage={setLeadPage}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 p-3 xl:hidden">
              {rows.map((property) => (
                <PropertyMobileCard
                  key={property.property_id}
                  property={property}
                  expanded={expandedPropertyId === property.property_id}
                  onToggle={() => toggleProperty(property.property_id)}
                >
                  {expandedPropertyId === property.property_id && (
                    <LeadDetailPanel
                      property={property}
                      rows={leadRows}
                      total={leadTotal}
                      page={leadPage}
                      maxPage={maxLeadPage}
                      loading={leadLoading}
                      error={leadError}
                      onPage={setLeadPage}
                      mobile
                    />
                  )}
                </PropertyMobileCard>
              ))}
            </div>
          </>
        )}

        {!loading && total > 0 && (
          <Pagination page={page} maxPage={maxPage} onPage={changePage} label="property results" />
        )}
      </section>
    </div>
  );
}

function SummaryCards({ summary, loading }) {
  const cards = [
    { label: "Matching leads", value: summary.total_leads, icon: UsersRound, tone: "bg-primary/10 text-primary" },
    { label: "Properties", value: summary.properties_with_leads, icon: Building2, tone: "bg-success/10 text-success" },
    { label: "Unassigned", value: summary.unassigned_leads, icon: UserRoundCheck, tone: "bg-warning/10 text-warning-foreground" },
    { label: "Overdue follow-ups", value: summary.leads_with_overdue_followups, icon: CalendarClock, tone: "bg-destructive/10 text-destructive" },
  ];
  return (
    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Lead Explorer summary">
      {cards.map(({ label, value, icon: Icon, tone }) => (
        <article key={label} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
              {loading ? <Skeleton className="mt-2 h-8 w-20" /> : <p className="mt-1 text-2xl font-black text-foreground">{count(value)}</p>}
            </div>
            <span className={`rounded-xl p-2.5 ${tone}`}><Icon className="h-5 w-5" /></span>
          </div>
        </article>
      ))}
    </section>
  );
}

function FilterSelect({ label, value, onChange, options, disabled = false }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-bold text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm outline-none focus:border-primary focus:ring-1 focus:ring-ring disabled:cursor-wait disabled:opacity-60"
        title={`Filter by ${label.toLowerCase()}`}
      >
        {options.map((option) => <option key={`${label}-${option.value || "all"}`} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function DateFilter({ label, value, onChange, min, max }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-bold text-muted-foreground">{label}</span>
      <Input type="date" value={value} min={min} max={max} onChange={(event) => onChange(event.target.value)} title={label} />
    </label>
  );
}

function PropertyThumbnail({ property, className = "h-14 w-16" }) {
  const [failed, setFailed] = useState(false);
  const source = prefixUrl(property.thumbnail_url);
  const image = source && !failed;
  return (
    <Link
      to={propertyLink(property.property_id)}
      title={`Open property: ${property.title || property.property_code || "Untitled property"}`}
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-secondary ${className}`}
    >
      {image
        ? <img src={source} alt={`${property.title || "Property"} thumbnail`} loading="lazy" decoding="async" className="h-full w-full object-cover" onError={() => setFailed(true)} />
        : <ImageOff className="h-5 w-5 text-muted-foreground" aria-label="Property image unavailable" />}
    </Link>
  );
}

function PropertyDesktopRow({ property, expanded, onToggle }) {
  const agents = parseRelationshipList(property.assigned_agents);
  const offices = parseRelationshipList(property.assigned_offices);
  return (
    <tr className={`border-b border-border/70 align-top transition-colors hover:bg-secondary/30 ${expanded ? "bg-primary/[0.035]" : ""}`}>
      <td className="px-4 py-4">
        <div className="flex min-w-[270px] gap-3">
          <PropertyThumbnail property={property} />
          <div className="min-w-0">
            <Link to={propertyLink(property.property_id)} title="Open this property record" className="line-clamp-2 font-bold text-foreground hover:text-primary hover:underline">
              {property.title || "Untitled property"}
            </Link>
            <p className="mt-1 text-xs text-muted-foreground">{property.property_code || "No code"} · {displayLabel(property.property_type)}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{[property.locality, property.city].filter(Boolean).join(", ") || "Location unavailable"}</p>
            <div className="mt-2 flex flex-wrap gap-1"><StatusBadge value={property.property_status} /><Badge variant="outline">{formatPrice(property.price)}</Badge></div>
          </div>
        </div>
      </td>
      <td className="px-3 py-4">
        <div className="min-w-[150px] space-y-2 text-xs">
          <LabeledEntity label="Owner" id={property.owner_user_id} name={property.owner_name} href={userLink} />
          <LabeledEntity label="Agent" id={property.listing_agent_id} name={property.listing_agent_name} href={userLink} />
          <LabeledEntity label="Office" id={property.property_office_id} name={property.property_office_name} href={officeLink} />
        </div>
      </td>
      <td className="px-3 py-4">
        <RelationshipLinks label="Agents" items={agents} idKey="user_id" href={userLink} empty="No assigned agent" />
        <RelationshipLinks label="Offices" items={offices} idKey="office_id" href={officeLink} empty="No assigned office" />
      </td>
      <td className="px-3 py-4">
        <p className="text-xl font-black text-foreground">{count(property.lead_count)}</p>
        <p className="mt-1 text-xs text-muted-foreground">{count(property.active_lead_count)} active</p>
        <p className="mt-1 text-xs text-muted-foreground">{count(property.buyer_account_count)} buyer accounts</p>
        {Number(property.unassigned_lead_count || 0) > 0 && <p className="mt-1 text-xs font-extrabold text-amber-700 dark:text-amber-300">{count(property.unassigned_lead_count)} unassigned</p>}
      </td>
      <td className="px-3 py-4 text-xs">
        <p className={Number(property.overdue_followup_count || 0) > 0 ? "font-bold text-destructive" : "text-muted-foreground"}>
          {count(property.overdue_followup_count)} overdue
        </p>
        <p className="mt-1 text-muted-foreground">{count(property.site_visit_count)} site visits</p>
        <p className="mt-1 text-muted-foreground">Next: {formatDateTime(property.next_followup_at)}</p>
      </td>
      <td className="px-3 py-4 text-xs text-muted-foreground">{formatDateTime(property.last_activity_at)}</td>
      <td className="px-4 py-4 text-right">
        <Button type="button" size="sm" variant={expanded ? "secondary" : "outline"} onClick={onToggle} title={expanded ? "Hide buyer lead details" : "Show buyer contact and lead details"}>
          {expanded ? <ChevronUp /> : <ChevronDown />} {expanded ? "Hide" : "View buyers"}
        </Button>
      </td>
    </tr>
  );
}

function PropertyMobileCard({ property, expanded, onToggle, children }) {
  const agents = parseRelationshipList(property.assigned_agents);
  const offices = parseRelationshipList(property.assigned_offices);
  return (
    <article className={`overflow-hidden rounded-2xl border bg-background ${expanded ? "border-primary/30 shadow-sm" : "border-border"}`}>
      <div className="p-4">
        <div className="flex gap-3">
          <PropertyThumbnail property={property} className="h-16 w-20" />
          <div className="min-w-0 flex-1">
            <Link to={propertyLink(property.property_id)} title="Open this property record" className="line-clamp-2 font-bold text-foreground hover:text-primary hover:underline">{property.title || "Untitled property"}</Link>
            <p className="mt-1 text-xs text-muted-foreground">{property.property_code || "No code"} · {displayLabel(property.property_type)}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{[property.locality, property.city].filter(Boolean).join(", ") || "Location unavailable"}</p>
            <div className="mt-2 flex flex-wrap gap-1"><StatusBadge value={property.property_status} /><Badge variant="outline">{formatPrice(property.price)}</Badge></div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <MetricCell label="Leads" value={count(property.lead_count)} />
          <MetricCell label="Buyer accounts" value={count(property.buyer_account_count)} />
          <MetricCell label="Active leads" value={count(property.active_lead_count)} />
          <MetricCell label="Unassigned" value={count(property.unassigned_lead_count)} warning={Number(property.unassigned_lead_count || 0) > 0} />
          <MetricCell label="Site visits" value={count(property.site_visit_count)} />
          <MetricCell label="Overdue" value={count(property.overdue_followup_count)} danger={Number(property.overdue_followup_count || 0) > 0} />
        </div>
        <div className="mt-4 space-y-2 border-t border-border pt-3 text-xs">
          <LabeledEntity label="Owner" id={property.owner_user_id} name={property.owner_name} href={userLink} />
          <LabeledEntity label="Listing agent" id={property.listing_agent_id} name={property.listing_agent_name} href={userLink} />
          <LabeledEntity label="Office" id={property.property_office_id} name={property.property_office_name} href={officeLink} />
          <RelationshipLinks label="Assigned agents" items={agents} idKey="user_id" href={userLink} empty="No assigned agent" compact />
          <RelationshipLinks label="Assigned offices" items={offices} idKey="office_id" href={officeLink} empty="No assigned office" compact />
        </div>
        <Button type="button" className="mt-4 w-full" variant={expanded ? "secondary" : "outline"} onClick={onToggle} title={expanded ? "Back to the property list" : "Show buyer contact and lead details"}>
          {expanded ? <ChevronUp /> : <ChevronDown />} {expanded ? "Back to property list" : "View buyer details"}
        </Button>
      </div>
      {children}
    </article>
  );
}

function LeadDetailPanel({ property, rows, total, page, maxPage, loading, error, onPage, mobile = false }) {
  return (
    <div className={`${mobile ? "border-t border-border bg-secondary/20 p-3" : "border-y border-primary/20 bg-secondary/20 p-4"}`}>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-base font-black text-foreground">Buyer lead details</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{count(total)} matching leads for {property.title || property.property_code || "this property"}</p>
        </div>
        <Link to={propertyLink(property.property_id)} title="Open the full property record" className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
          Open property <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">{error}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-background p-5 text-center text-sm text-muted-foreground">No buyer leads match the active filters.</div>
      ) : mobile ? (
        <div className="space-y-3">{rows.map((lead) => <LeadMobileCard key={lead.lead_id} lead={lead} />)}</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-background">
          <table className="w-full min-w-[1280px] text-left text-xs">
            <thead className="bg-secondary/70 font-bold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-3">Buyer</th>
                <th className="px-3 py-3">Phone & email</th>
                <th className="px-3 py-3">Status & source</th>
                <th className="px-3 py-3">Agent & office</th>
                <th className="px-3 py-3">Created</th>
                <th className="px-3 py-3">Follow-up</th>
                <th className="px-3 py-3">Site visit</th>
                <th className="px-3 py-3 text-right">CRM record</th>
              </tr>
            </thead>
            <tbody>{rows.map((lead) => <LeadDesktopRow key={lead.lead_id} lead={lead} />)}</tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && (
        <Pagination page={page} maxPage={maxPage} onPage={onPage} label="buyer leads" compact />
      )}
    </div>
  );
}

function LeadDesktopRow({ lead }) {
  return (
    <tr className="border-b border-border/70 align-top last:border-0 hover:bg-secondary/30">
      <td className="px-3 py-3">
        <BuyerName lead={lead} />
        <p className="mt-1 font-mono text-[10px] text-muted-foreground">{String(lead.lead_id).slice(0, 8)}</p>
      </td>
      <td className="px-3 py-3"><BuyerContact lead={lead} /></td>
      <td className="px-3 py-3"><LeadStatusSource lead={lead} /></td>
      <td className="px-3 py-3">
        <LabeledEntity label="Agent" id={lead.assigned_agent_id} name={lead.assigned_agent_name} href={userLink} fallback="Unassigned" warning={!lead.assigned_agent_id} />
        <div className="mt-2"><LabeledEntity label="Office" id={lead.assigned_office_id} name={lead.assigned_office_name} href={officeLink} /></div>
      </td>
      <td className="px-3 py-3 text-muted-foreground">{formatDateTime(lead.created_at)}</td>
      <td className="px-3 py-3">
        <p className={lead.followup_overdue ? "font-bold text-destructive" : "text-muted-foreground"}>{formatDateTime(lead.next_followup_at)}</p>
        {lead.followup_status && <p className="mt-1 text-[10px] text-muted-foreground">{displayLabel(lead.followup_status)}</p>}
      </td>
      <td className="px-3 py-3">
        <p className="font-semibold text-foreground">{count(lead.site_visit_count)} visits</p>
        <p className="mt-1 text-muted-foreground">{displayLabel(lead.latest_site_visit_status)}</p>
        <p className="mt-1 text-[10px] text-muted-foreground">{formatDateTime(lead.latest_site_visit_at)}</p>
      </td>
      <td className="px-3 py-3 text-right">
        <Button asChild size="sm" variant="outline"><Link to={leadLink(lead.lead_id)} title="Open this lead in the CRM Kanban">Open CRM <ExternalLink /></Link></Button>
      </td>
    </tr>
  );
}

function LeadMobileCard({ lead }) {
  return (
    <article className="rounded-xl border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div><BuyerName lead={lead} /><p className="mt-1 font-mono text-[10px] text-muted-foreground">Lead {String(lead.lead_id).slice(0, 8)}</p></div>
        <StatusBadge value={lead.lead_status} />
      </div>
      <div className="mt-3 rounded-lg bg-secondary/60 p-3"><BuyerContact lead={lead} /></div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-xs">
        <div><dt className="text-muted-foreground">Source</dt><dd className="mt-1"><SourceLink lead={lead} /></dd></div>
        <div><dt className="text-muted-foreground">Created</dt><dd className="mt-1 font-semibold">{formatDateTime(lead.created_at, true)}</dd></div>
        <div><dt className="text-muted-foreground">Agent</dt><dd className="mt-1"><LabeledEntity id={lead.assigned_agent_id} name={lead.assigned_agent_name} href={userLink} fallback="Unassigned" warning={!lead.assigned_agent_id} /></dd></div>
        <div><dt className="text-muted-foreground">Office</dt><dd className="mt-1"><LabeledEntity id={lead.assigned_office_id} name={lead.assigned_office_name} href={officeLink} /></dd></div>
        <div><dt className="text-muted-foreground">Follow-up</dt><dd className={`mt-1 font-semibold ${lead.followup_overdue ? "text-destructive" : ""}`}>{formatDateTime(lead.next_followup_at, true)}</dd></div>
        <div><dt className="text-muted-foreground">Site visits</dt><dd className="mt-1 font-semibold">{count(lead.site_visit_count)} · {displayLabel(lead.latest_site_visit_status)}</dd></div>
      </dl>
      <Button asChild size="sm" variant="outline" className="mt-4 w-full"><Link to={leadLink(lead.lead_id)} title="Open this lead in the CRM Kanban">Open CRM record <ExternalLink /></Link></Button>
    </article>
  );
}

function BuyerName({ lead }) {
  if (lead.buyer_user_id) {
    return <Link to={userLink(lead.buyer_user_id)} title="Open this buyer account" className="inline-flex items-center gap-1.5 font-bold text-foreground hover:text-primary hover:underline"><CircleUserRound className="h-4 w-4" />{lead.buyer_name || "Unknown buyer"}</Link>;
  }
  return <span className="inline-flex items-center gap-1.5 font-bold text-foreground"><CircleUserRound className="h-4 w-4" />{lead.buyer_name || "Unknown buyer"}</span>;
}

function BuyerContact({ lead }) {
  return (
    <div className="min-w-[190px] space-y-1.5">
      {lead.buyer_phone ? (
        <a href={`tel:${lead.buyer_phone}`} title={`Call ${lead.buyer_name || "buyer"}`} className="flex items-center gap-1.5 font-bold text-primary hover:underline"><Phone className="h-3.5 w-3.5" />{lead.buyer_phone}</a>
      ) : <span className="flex items-center gap-1.5 text-muted-foreground"><Phone className="h-3.5 w-3.5" />Phone unavailable</span>}
      {lead.buyer_email ? (
        <a href={`mailto:${lead.buyer_email}`} title={`Email ${lead.buyer_name || "buyer"}`} className="flex max-w-[240px] items-center gap-1.5 break-all text-muted-foreground hover:text-primary hover:underline"><Mail className="h-3.5 w-3.5 shrink-0" />{lead.buyer_email}</a>
      ) : <span className="flex items-center gap-1.5 text-muted-foreground"><Mail className="h-3.5 w-3.5" />Email unavailable</span>}
    </div>
  );
}

function LeadStatusSource({ lead }) {
  return <div className="space-y-2"><StatusBadge value={lead.lead_status} /><div><SourceLink lead={lead} /></div></div>;
}

function SourceLink({ lead }) {
  if (String(lead.source || "").toUpperCase() === "PLOT_DEMAND" && lead.plot_demand_id) {
    return <Link to={plotDemandLink(lead.plot_demand_id)} title="Open this Plots on Demand request" className="font-bold text-primary hover:underline">Plots on Demand</Link>;
  }
  return <span className="font-semibold text-muted-foreground">{displayLabel(lead.source)}</span>;
}

function StatusBadge({ value }) {
  return <Badge variant="outline" className={statusTone(value)}>{displayLabel(value)}</Badge>;
}

function LabeledEntity({ label, id, name, href, fallback = "Not assigned", warning = false }) {
  return (
    <div className="min-w-0">
      {label && <span className="mr-1 text-muted-foreground">{label}:</span>}
      {id ? (
        <Link to={href(id)} title={`Open ${label ? label.toLowerCase() : "record"}: ${name || "Unknown"}`} className="font-bold text-foreground hover:text-primary hover:underline">{name || "Unknown"}</Link>
      ) : <span className={warning ? "font-bold text-warning-foreground" : "text-muted-foreground"}>{fallback}</span>}
    </div>
  );
}

function RelationshipLinks({ label, items, idKey, href, empty, compact = false }) {
  return (
    <div className={compact ? "" : "mb-2 min-w-[160px] last:mb-0"}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
      {items.length ? (
        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1">
          {items.slice(0, 3).map((item) => (
            <Link key={item[idKey]} to={href(item[idKey])} title={`Open ${item.name || label}`} className="text-xs font-bold text-foreground hover:text-primary hover:underline">{item.name || "Unknown"}</Link>
          ))}
          {items.length > 3 && <span className="text-xs text-muted-foreground">+{items.length - 3} more</span>}
        </div>
      ) : <p className="mt-1 text-xs text-muted-foreground">{empty}</p>}
    </div>
  );
}

function MetricCell({ label, value, warning = false, danger = false }) {
  return <div className="rounded-lg bg-secondary/60 p-2"><p className="text-muted-foreground">{label}</p><p className={`mt-0.5 font-black ${danger ? "text-destructive" : warning ? "text-warning-foreground" : "text-foreground"}`}>{value}</p></div>;
}

function Pagination({ page, maxPage, onPage, label, compact = false }) {
  return (
    <div className={`flex items-center justify-between gap-3 border-t border-border ${compact ? "mt-3 pt-3" : "p-4 sm:p-5"}`}>
      <p className="text-xs text-muted-foreground">Page {page} of {maxPage} {label}</p>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} title={`Previous page of ${label}`}><ChevronLeft /> Previous</Button>
        <Button type="button" size="sm" variant="outline" disabled={page >= maxPage} onClick={() => onPage(page + 1)} title={`Next page of ${label}`}>Next <ChevronRight /></Button>
      </div>
    </div>
  );
}

function PropertySkeletons() {
  return <div className="space-y-3 p-4">{[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-24 w-full rounded-2xl" />)}</div>;
}

function EmptyState({ onClear }) {
  return (
    <div className="flex flex-col items-center px-5 py-16 text-center">
      <span className="rounded-2xl bg-secondary p-4 text-muted-foreground"><Building2 className="h-7 w-7" /></span>
      <h3 className="mt-4 font-display text-lg font-black text-foreground">No matching property leads</h3>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">Try a wider date range or clear one or more filters. The existing Kanban data has not been changed.</p>
      <Button type="button" variant="outline" className="mt-4" onClick={onClear} title="Clear every Lead Explorer filter"><RotateCcw /> Clear filters</Button>
    </div>
  );
}
